import { t } from "i18next";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";
import { GetReport as GetFacilityReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetReport as GetClientReport } from "api/rpc/2024-04/clientAdmin/report/report";
import { StatusCode } from "api/protocols";
import { TReportAdminScope } from "./reportCatalog.types";
import { getQuickExportTemplateId } from "./quickExport/quickExportConfig";
import { enrichQuickExportParams } from "./quickExport/enrichQuickExportParams";
import { buildReportTemplateParams, buildTodayDateRange } from "./quickExport/quickExportTemplates";
import { TQuickExportParamsResult } from "./quickExport/quickExport.types";

export type { TQuickExportParamsResult as TReportDefaultParamsResult } from "./quickExport/quickExport.types";
export { isQuickExportParamsFailure as isReportDefaultParamsFailure } from "./quickExport/quickExport.types";

interface IBuildReportDefaultParamsOptions {
  scope: TReportAdminScope;
  reportId: number;
  code: string;
  isDedicatedRoute: boolean;
}

function configurationsNeedFacilityId(configurations: IConfiguration[]): boolean {
  return configurations.some(
    config =>
      config.option_name === "facility_id" && config.data_type === "integer" && config.required === 1,
  );
}

function buildParamsFromConfigurations(
  reportId: number,
  configurations: IConfiguration[],
): TQuickExportParamsResult {
  const params: Record<string, unknown> = { report_id: reportId };
  const dateRange = buildTodayDateRange();
  const today = dateRange[0];

  for (const config of configurations) {
    const { option_name: optionName, data_type: dataType, required } = config;

    if (dataType === "date_time_array" && optionName === "date_range") {
      params.date_range = dateRange;
      continue;
    }

    if (dataType === "date_time" && optionName === "date") {
      params.date = today;
      continue;
    }

    if (dataType === "integer_array") {
      params[optionName] = [];
      continue;
    }

    if (dataType === "integer" && optionName.includes("grat")) {
      params[optionName] = 0;
      continue;
    }

    if (dataType === "integer" && optionName === "facility_id") {
      continue;
    }

    if (required === 1) {
      return { ok: false, errorMessage: t("Open report to configure filters") };
    }
  }

  if (!params.date_range && !params.date) {
    params.date_range = dateRange;
  }

  return { ok: true, params };
}

export async function buildReportDefaultParams(
  options: IBuildReportDefaultParamsOptions,
): Promise<TQuickExportParamsResult> {
  const { scope, reportId, code, isDedicatedRoute } = options;

  if (isDedicatedRoute) {
    const templateId = getQuickExportTemplateId(scope, code);
    const baseParams = buildReportTemplateParams(templateId, reportId);
    return enrichQuickExportParams(scope, code, baseParams, { isDedicatedRoute: true });
  }

  const reportRes =
    scope === "facility"
      ? await GetFacilityReport({ id: reportId }, false)
      : await GetClientReport({ id: reportId }, false);

  if (reportRes.status !== StatusCode.OK) {
    return { ok: false, errorMessage: t("Error loading report") };
  }

  const report = reportRes.data?.[0];
  if (!report) {
    return { ok: false, errorMessage: t("Error loading report") };
  }

  const configurations = [...(report.configurations ?? [])].sort((a, b) => a.position - b.position);
  const paramsResult = buildParamsFromConfigurations(reportId, configurations);

  if (!paramsResult.ok) {
    return paramsResult;
  }

  return enrichQuickExportParams(scope, code, paramsResult.params, {
    needsFacilityId: configurationsNeedFacilityId(configurations),
  });
}
